import { Component, Input, ElementRef, Renderer2, OnDestroy, HostListener } from '@angular/core';

@Component({
    selector: 'info-tooltip',
    templateUrl: './info-tooltip.component.html',
    styleUrls: ['./info-tooltip.component.scss'],
    standalone: false
})
export class InfoTooltipComponent implements OnDestroy {
  @Input() infoText: string = 'Default information text. This is a longer text that demonstrates scrolling and responsiveness.';

  private tooltipBoxElement: HTMLElement | null = null;
  private static readonly MAX_Z_INDEX = 9999;
  // Mirrors max-width in info-tooltip.component.scss
  private static readonly MAX_BOX_WIDTH = 550;
  private hideTimeout: any;
  private static readonly HIDE_DELAY = 100;

  constructor(
    private el: ElementRef, 
    private renderer: Renderer2
  ) { }

  // ----------------------------------------------------
  // Lifecycle & Cleanup
  // ----------------------------------------------------

  ngOnDestroy(): void {
    clearTimeout(this.hideTimeout);
    if (this.tooltipBoxElement && this.tooltipBoxElement.parentNode) {
      this.renderer.removeChild(this.tooltipBoxElement.parentNode, this.tooltipBoxElement);
    }
  }

  // ----------------------------------------------------
  // Responsiveness (Viewport & Scroll/Resize)
  // ----------------------------------------------------

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onWindowChange(): void {
    // Only update position if the tooltip is currently visible
    if (this.tooltipBoxElement && this.tooltipBoxElement.style.visibility === 'visible') {
      this.updatePosition();
    }
  }

  // ----------------------------------------------------
  // Visibility & Interaction
  // ----------------------------------------------------

  showTooltip(): void {
    clearTimeout(this.hideTimeout); 

    if (!this.tooltipBoxElement) {
      const box = this.el.nativeElement.querySelector('.tooltip-box');
      if (box) {
        this.tooltipBoxElement = box;
        // CRITICAL for stacking: Append to body
        this.renderer.appendChild(document.body, this.tooltipBoxElement);
        this.addTooltipListeners();
      }
    }
    
    if (this.tooltipBoxElement) {
      this.renderer.setStyle(this.tooltipBoxElement, 'z-index', InfoTooltipComponent.MAX_Z_INDEX);
      this.renderer.setStyle(this.tooltipBoxElement, 'visibility', 'visible');
      this.updatePosition(); // Initial position set
    }
  }

  delayHideTooltip(): void {
    clearTimeout(this.hideTimeout);
    this.hideTimeout = setTimeout(() => {
      this.hideTooltip();
    }, InfoTooltipComponent.HIDE_DELAY);
  }

  hideTooltip(): void {
    if (this.tooltipBoxElement) {
       this.renderer.setStyle(this.tooltipBoxElement, 'visibility', 'hidden'); 
    }
  }

  // Keeps tooltip open if cursor enters the body-appended box
  private onTooltipEnter = (): void => {
    clearTimeout(this.hideTimeout);
    if (this.tooltipBoxElement) {
        this.renderer.setStyle(this.tooltipBoxElement, 'visibility', 'visible');
    }
  }

  // Starts hide process when cursor leaves the body-appended box
  private onTooltipLeave = (): void => {
    this.delayHideTooltip();
  }

  private addTooltipListeners(): void {
    if (this.tooltipBoxElement) {
        this.renderer.listen(this.tooltipBoxElement, 'mouseenter', this.onTooltipEnter);
        this.renderer.listen(this.tooltipBoxElement, 'mouseleave', this.onTooltipLeave);
    }
  }

  // ----------------------------------------------------
  // Positioning Logic (The "Responsive Scroll" part)
  // ----------------------------------------------------

  updatePosition(): void {
    if (!this.tooltipBoxElement) return;

    const iconRect: DOMRect = this.el.nativeElement.querySelector('.info-icon').getBoundingClientRect();

    // 1. Boundary Check (Left/Right) - clamp to the containing dialog when the
    // icon sits inside one, otherwise to the viewport.
    const padding = 10;
    const container: HTMLElement | null = this.el.nativeElement.closest('.p-dialog');
    const containerRect = container ? container.getBoundingClientRect() : null;
    const minLeft = (containerRect ? containerRect.left : 0) + window.scrollX + padding;
    const maxRight = (containerRect ? containerRect.right : window.innerWidth) + window.scrollX - padding;

    // Never let the box be wider than the space it has to sit in. Only ever
    // narrows: the stylesheet cap still applies when there is room for it.
    const available = Math.max(maxRight - minLeft, 0);
    this.renderer.setStyle(this.tooltipBoxElement, 'max-width',
      `${Math.min(InfoTooltipComponent.MAX_BOX_WIDTH, available)}px`);

    const boxWidth: number = this.tooltipBoxElement.offsetWidth;
    const boxHeight: number = this.tooltipBoxElement.offsetHeight;

    let top = iconRect.top + window.scrollY - boxHeight - 8; // Default: 8px above the icon
    let left = iconRect.left + window.scrollX + (iconRect.width / 2) - (boxWidth / 2);

    // If box goes off the right edge: shift left
    if (left + boxWidth > maxRight) {
      left = maxRight - boxWidth;
    }

    // If box goes off the left edge: shift right
    if (left < minLeft) {
      left = minLeft;
    }

    // 2. Viewport Boundary Check (Top) - Important for responsiveness
    // If the tooltip would go off the top of the screen, flip it to the bottom
    if (top < window.scrollY + padding) {
        top = iconRect.bottom + window.scrollY + 8; // Position 8px below the icon
        this.renderer.addClass(this.tooltipBoxElement, 'bottom-aligned');
    } else {
        this.renderer.removeClass(this.tooltipBoxElement, 'bottom-aligned');
    }

    this.renderer.setStyle(this.tooltipBoxElement, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltipBoxElement, 'left', `${left}px`);
  }
}